import { useState, type FormEvent } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { APP_LABELS, isAppKey, type AppKey, type AuditLogExportFormat } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { PageMessage } from '../AppHome.js';
import { ApiError } from '../../lib/api.js';
import {
  downloadAuditLog,
  fetchAuditLog,
  type AuditLogEntry,
  type AuditLogFilters,
} from '../../lib/audit-log-api.js';
import { t, formatDateTime } from '../../lib/i18n.js';

/**
 * The Audit & Compliance Log (spec §13.7).
 *
 * A window onto the evidence record and nothing more: there is no edit, no
 * delete and no "hide" anywhere on this screen, because the log has none of
 * those anywhere (Rule 2, ADR-0016). What it is careful to say:
 *
 *  - **Which entries you can see**, and why — an administrator sees everything;
 *    anyone else sees the applications their View All covers.
 *  - **That looking is recorded.** Every search and export writes its own entry.
 *  - **The retention policy as it actually is.** Nothing is deleted; old entries
 *    may be archived by operations after the period set in Master Data.
 */
export function AuditLog() {
  const params = useParams<{ app: string }>();
  const [draft, setDraft] = useState<AuditLogFilters>({});
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [open, setOpen] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<AuditLogExportFormat | null>(null);

  const log = useInfiniteQuery({
    queryKey: ['audit-log', filters],
    queryFn: ({ pageParam }) => fetchAuditLog(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Every read is recorded server-side; refetching on focus would write an
    // entry each time somebody switched windows.
    refetchOnWindowFocus: false,
  });

  const app = params.app;
  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  const first = log.data?.pages[0];
  const entries = log.data?.pages.flatMap((page) => page.entries) ?? [];

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    setFilters({ ...draft });
    setOpen(null);
  };

  const onExport = async (format: AuditLogExportFormat) => {
    setExportError(null);
    setExporting(format);
    try {
      await downloadAuditLog(filters, format);
    } catch (caught) {
      setExportError(caught instanceof ApiError ? caught.message : 'The export could not be produced.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Settings', 'Audit & Compliance Log']} />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Audit & Compliance Log")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Every recorded action. Nothing here can be changed or deleted, by anyone. Searching and exporting are recorded too.")}
        </p>

        {log.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {log.error && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {log.error instanceof ApiError && log.error.status !== 403
                ? log.error.message
                : t("Reading the audit log needs access administration, or View All across an application.")}
            </p>
          </section>
        )}

        {first && (
          <>
            <p className="mt-3 text-sm text-content-secondary" data-testid="audit-scope">
              {first.access.everything
                ? t("You can see every entry, including sign-ins and access changes.")
                : t("You can see entries for {0}. Sign-ins and access changes are for access administrators.", { 0: first.access.apps.map((key) => APP_LABELS[key]).join(', ') })}
            </p>
            <p className="mt-1 text-sm text-content-secondary" data-testid="audit-retention">
              {first.retention.onlineMonths === null
                ? t("Retention: no period set — every entry stays online. Entries are never deleted.")
                : t("Retention: entries stay online for {0} months, after which they may be archived. Entries are never deleted.", { 0: first.retention.onlineMonths })}
            </p>

            <form onSubmit={onSearch} className="mt-6 flex flex-wrap items-end gap-3 rounded-md border border-border bg-surface p-4">
              <Field label={t("From")}>
                <input
                  type="date"
                  value={draft.from ?? ''}
                  onChange={(event) => setDraft({ ...draft, from: event.target.value })}
                  className="rounded border border-border px-2 py-1"
                />
              </Field>
              <Field label={t("To")}>
                <input
                  type="date"
                  value={draft.to ?? ''}
                  onChange={(event) => setDraft({ ...draft, to: event.target.value })}
                  className="rounded border border-border px-2 py-1"
                />
              </Field>
              <Field label={t("Application")}>
                <select
                  value={draft.app ?? ''}
                  onChange={(event) => setDraft({ ...draft, app: event.target.value })}
                  className="rounded border border-border px-2 py-1"
                >
                  <option value="">{t("All I can see")}</option>
                  {first.access.apps.map((key) => (
                    <option key={key} value={key}>
                      {t(APP_LABELS[key])}
                    </option>
                  ))}
                  {first.access.everything && <option value="platform">{t("Platform (sign-ins, access)")}</option>}
                </select>
              </Field>
              <Field label={t("Action")}>
                <input
                  type="text"
                  placeholder={t("e.g. approve")}
                  value={draft.action ?? ''}
                  onChange={(event) => setDraft({ ...draft, action: event.target.value.trim() })}
                  className="w-32 rounded border border-border px-2 py-1"
                />
              </Field>
              <Field label={t("Search record or person")}>
                <input
                  type="search"
                  value={draft.search ?? ''}
                  onChange={(event) => setDraft({ ...draft, search: event.target.value })}
                  className="w-56 rounded border border-border px-2 py-1"
                />
              </Field>
              <button type="submit" className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white">
                {t("Search")}
              </button>

              <div className="ms-auto flex gap-2">
                {(['csv', 'xlsx', 'pdf'] as const).map((format) => (
                  <button
                    key={format}
                    type="button"
                    disabled={exporting !== null}
                    onClick={() => void onExport(format)}
                    className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-60"
                  >
                    {exporting === format
                      ? t("Exporting…")
                      : format === 'csv'
                        ? t("Export CSV")
                        : format === 'xlsx'
                          ? t("Export Excel")
                          : t("Export PDF")}
                  </button>
                ))}
              </div>
            </form>

            {exportError && (
              <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--tone-rejected)' }}>
                {exportError}
              </p>
            )}

            {entries.length === 0 ? (
              <p className="mt-6 text-sm text-content-secondary">{t("No entries match these filters.")}</p>
            ) : (
              <table className="mt-6 w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-start text-content-secondary">
                    <th className="py-2 pe-3 text-start font-medium">{t("When")}</th>
                    <th className="py-2 pe-3 text-start font-medium">{t("Who")}</th>
                    <th className="py-2 pe-3 text-start font-medium">{t("Action")}</th>
                    <th className="py-2 pe-3 text-start font-medium">{t("Application")}</th>
                    <th className="py-2 text-start font-medium">{t("Record")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <EntryRow
                      key={entry.id}
                      entry={entry}
                      open={open === entry.id}
                      onToggle={() => setOpen(open === entry.id ? null : entry.id)}
                    />
                  ))}
                </tbody>
              </table>
            )}

            {log.hasNextPage && (
              <button
                type="button"
                onClick={() => void log.fetchNextPage()}
                disabled={log.isFetchingNextPage}
                className="mt-4 rounded border border-border px-3 py-1.5 text-sm"
              >
                {log.isFetchingNextPage ? t("Loading…") : t("Load older entries")}
              </button>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col text-xs">
      <span className="mb-1 text-content-secondary">{label}</span>
      {children}
    </label>
  );
}

const humanise = (action: string) => action.replace(/_/g, ' ');

function EntryRow({ entry, open, onToggle }: { entry: AuditLogEntry; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="border-b border-border align-top">
        <td className="py-2 pe-3 whitespace-nowrap">{formatDateTime(entry.occurredAt)}</td>
        <td className="py-2 pe-3">
          {entry.actorName ?? '—'}
          {entry.onBehalfOfName && (
            <span className="block text-xs text-content-secondary">{t("on behalf of")}{' '}{entry.onBehalfOfName}</span>
          )}
        </td>
        <td className="py-2 pe-3">{humanise(entry.action)}</td>
        <td className="py-2 pe-3">{entry.app ? APP_LABELS[entry.app] : t("Platform")}</td>
        <td className="py-2">
          <button type="button" onClick={onToggle} aria-expanded={open} className="text-start text-ag-purple underline">
            {entry.entityLabel ?? entry.entityType ?? t("Details")}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border bg-surface-sunken">
          <td colSpan={5} className="p-3 text-xs">
            <dl className="m-0 grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
              <Detail label={t("Record type")} value={entry.entityType} />
              <Detail label={t("Record id")} value={entry.entityId} />
              <Detail label={t("Signed in with")} value={entry.actorAuthSource} />
              <Detail label={t("Source IP")} value={entry.sourceIp} />
              <Detail label={t("Request id")} value={entry.requestId} />
            </dl>
            {entry.beforeState && <Json label={t("Before")} value={entry.beforeState} />}
            {entry.afterState && <Json label={t("After")} value={entry.afterState} />}
            {Object.keys(entry.metadata ?? {}).length > 0 && <Json label={t("Details")} value={entry.metadata} />}
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <>
      <dt className="text-content-secondary">{label}</dt>
      <dd className="m-0 break-all">{value}</dd>
    </>
  );
}

function Json({ label, value }: { label: string; value: Record<string, unknown> }) {
  return (
    <div className="mt-2">
      <p className="m-0 font-medium">{label}</p>
      <pre className="m-0 mt-1 overflow-x-auto whitespace-pre-wrap rounded bg-surface p-2">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
